import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Stage {
  tag: string;
  title: string;
  body: string;
  narrative: string;
  clock: string;
}

interface Kpi {
  ex: string;
  ok: string;
  settle: string;
}

const SpineStory: React.FC = () => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const stages = t('spine.stages', { returnObjects: true }) as Stage[];
  const kpis = t('spine.kpis', { returnObjects: true }) as Kpi[];
  const [active, setActive] = useState(2);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      setActive((current) => (current + 1) % 5);
    }, 3200);
    return () => window.clearInterval(id);
  }, [reduce]);

  if (!Array.isArray(stages) || !Array.isArray(kpis)) return null;
  const stage = stages[active] || stages[0];
  const kpi = kpis[active] || kpis[0];

  return (
    <div className="grid items-stretch gap-4 lg:grid-cols-[1.4fr_0.7fr]">
      <div className="relative">
        <div className="absolute bottom-4 left-3 top-4 w-px bg-line" aria-hidden="true">
          <div
            className="w-px bg-gradient-to-b from-amber to-mint transition-all duration-500"
            style={{ height: `${((active + 1) / stages.length) * 100}%` }}
          />
        </div>
        <ol className="space-y-3">
          {stages.map((item, index) => {
            const selected = index === active;
            return (
              <li key={item.tag}>
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  aria-current={selected ? 'step' : undefined}
                  className={`w-full rounded-2xl border px-4 py-4 pl-8 text-left transition ${
                    selected ? 'border-amber/50 bg-panel shadow-card' : 'border-line bg-graphite/70 hover:border-amber/30'
                  }`}
                >
                  <span className="font-mono text-[0.68rem] uppercase tracking-wide text-amber">{item.tag}</span>
                  <span className="mt-1 block font-display text-xl text-paper">{item.title}</span>
                  <span className="mt-1 block text-sm text-muted">{item.body}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      <aside className="rounded-2xl border border-line bg-panel p-5" aria-live="polite">
        <header className="flex items-center justify-between gap-3">
          <p className="font-mono text-[0.68rem] uppercase tracking-[0.08em] text-stamp">{t('spine.panelKicker')}</p>
          <p className="font-mono text-sm text-mint">{stage.clock}</p>
        </header>
        <p className="mt-4 font-display text-2xl leading-snug text-paper">{stage.narrative}</p>
        <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-ink">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber to-mint transition-all duration-500"
            style={{ width: `${((active + 1) / stages.length) * 100}%` }}
          />
        </div>
        <ul className="mt-5 divide-y divide-line text-sm">
          <li className="flex items-center justify-between py-2 text-muted">
            {t('spine.kpiEx')} <strong className="font-mono text-paper">{kpi.ex}</strong>
          </li>
          <li className="flex items-center justify-between py-2 text-muted">
            {t('spine.kpiOk')} <strong className="font-mono text-paper">{kpi.ok}</strong>
          </li>
          <li className="flex items-center justify-between py-2 text-muted">
            {t('spine.kpiSettle')} <strong className="font-mono text-paper">{kpi.settle}</strong>
          </li>
        </ul>
      </aside>
    </div>
  );
};

export default SpineStory;
